import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

// Decline with a managed reason (Admin-maintained labels, fixed codes) and optional client-visible feedback. Used by clients and by staff
// recording on the client's behalf; the server keeps who decided and refuses inactive reasons or stale offer versions.
export function DeclineOfferDialog({ open, onOpenChange, onSubmit, onBehalf = false }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/enquiries/offer-decline-reasons").then((r) => setReasons(r.data.items)).catch(() => setReasons([])); }, [open]);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit({ decline_reason: reason, client_feedback: feedback.trim() || null }); setReason(""); setFeedback(""); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record the decline"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="decline-offer-dialog">
        <DialogHeader><DialogTitle>Decline this offer</DialogTitle><DialogDescription>{onBehalf ? "Recorded by Jigsaw on the client's behalf — the record shows who recorded it." : "Tell us why so we can find a better option."} Feedback is visible to the client and to Jigsaw.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}><SelectTrigger data-testid="decline-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
              <SelectContent>{reasons.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label htmlFor="decline-feedback">Feedback {reason === "OTHER" ? "(required for Other)" : "(optional)"}</Label>
            <Textarea id="decline-feedback" data-testid="decline-feedback" maxLength={1000} rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="decline-submit" disabled={busy || !reason || (reason === "OTHER" && !feedback.trim())} className="bg-jigsaw-danger text-white hover:bg-jigsaw-danger/90" onClick={submit}>Decline offer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Read-only summary of a decline + feedback entries (client-visible). Internal notes are only ever present in the staff projection.
export function OfferFeedbackSummary({ offer: o, testId }) {
  if (!o.decline_reason_label && !o.client_feedback && !(o.client_feedback_entries || []).length) return null;
  return (
    <div className="mt-2 space-y-1 text-[12px]" data-testid={testId}>
      {o.decline_reason_label && <p className="text-jigsaw-navy">Reason: <span className="font-medium">{o.decline_reason_label}</span></p>}
      {o.client_feedback && <p className="text-jigsaw-muted">Feedback: {o.client_feedback}</p>}
      {(o.client_feedback_entries || []).map((f) => <p key={f.id} className="text-jigsaw-muted">Feedback{f.recorded_by_staff ? " (recorded by Jigsaw)" : ""} · {new Date(f.at).toLocaleDateString("en-GB")}: {f.text}</p>)}
    </div>
  );
}
